<template>
  <div
    class="flex flex-col gap-4 w-full"
  >
    <div class="flex items-center gap-2">
      <h1 class="text-text-primary text-3xl font-semibold">{{ $t('coach.match_restrat.title') }}</h1>
      <div class="has-tooltip">
        <img src="../../../../assets/icons/info-icon.svg" :alt="$t('coach.alt.avatar')" class="w-6 h-6 cursor-pointer" />
        <div class="tooltip bg-gray-900 rounded-lg p-2 ml-8 -mt-7">
          <p class="text-text-secondary text-xs">{{ $t('coach.match_restrat.tooltip') }}</p>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 w-full">
      <div class="flex gap-2 items-center">
        <div class="flex flex-col gap-2 flex-1">
          <h1 class="text-text-primary text-lg font-semibold">{{ $t('coach.match_restrat.rounds') }}</h1>
          <p class="text-text-secondary text-sm">{{ $t('coach.match_restrat.rounds_hint') }}</p>
        </div>
        <div class="flex gap-2">
          <button @click="startDemo(currentRound)" class="bg-primary-600 hover:bg-primary-500 duration-300 text-text-primary px-3 py-2 rounded-lg text-sm flex gap-2 items-center">
            <div v-if="loadingRound" class="flex justify-center items-center">
              <svg
                class="animate-spin h-full font-normal w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <HandThumbUpIcon v-else-if="loadingRoundSuccess" class="text-green-500 w-4 h-4" />
            <img v-else src="../../../../assets/icons/play.svg" :alt="$t('coach.alt.play')" class="w-4 h-4 brightness-200" />
            <h1 v-if="activeServer?.status === 'online'">{{ $t('coach.match_restrat.load_round') }}</h1>
            <h1 v-else>{{ $t('coach.match_restrat.start_demo') }}</h1>
          </button>
          <button v-if="!hide2DViewer" @click="open2DViewer" class="bg-gray-700 hover:bg-gray-600 duration-300 text-text-primary px-3 py-2 rounded-lg text-sm flex gap-2 items-center">
            <img src="../../../../assets/icons/play.svg" :alt="$t('coach.match_page.two_d_viewer')" class="w-4 h-4 brightness-200" />
            <h1>{{ $t('coach.match_page.two_d_viewer') }}</h1>
          </button>
        </div>
      </div>

      <div class="w-full flex-col gap-6 flex-1">
        <div class="flex overflow-x-auto pb-2 flex-1 w-full gap-px">

          <div class="flex flex-col divide-y divide-gray-950 text-text-secondary rounded-l-lg overflow-hidden shrink-0">
            <h1 class="text-text-secondary text-sm p-3 bg-gray-900 w-[144px] truncate">{{ matchData?.parsed_data?.team_info[1]?.team_name || $t('coach.match_restrat.team_2') }}</h1>
            <h1 class="text-text-secondary text-sm p-3 bg-gray-900 w-[144px] truncate">{{ matchData?.parsed_data?.team_info[0]?.team_name || $t('coach.match_restrat.team_1') }}</h1>
          </div>

          <div class="flex divide-x divide-gray-950 bg-gray-900 min-w-max shrink-0 overflow-hidden flex-1">

            <div class="flex divide-x divide-gray-950 min-w-max flex-1">
              <div
                v-for="(round, index) in firstHalfRounds"
                :key="index"
                @click="updateRound(index + 1)"
                class="flex flex-col p-3 text-text-secondary relative items-center justify-center w-12 shrink-0 cursor-pointer flex-1"
                :class="[currentRound === index + 1 ? 'bg-gradient-to-b from-gray-900 to-gray-800' : '']">
                <h1 class="text-text-secondary text-sm whitespace-nowrap">{{ index + 1 }}</h1>
                <img
                  :src="`/img/icons/${getRoundEndReasonString(round.winning_reason)}.svg`"
                  :alt="$t('coach.alt.round_win_icon')"
                  class="w-4 h-4 absolute"
                  :class="getWinIconPosition(round, index + 1)"
                  style="left: 50%; transform: translateX(-50%)"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-2 items-center justify-center px-3 shrink-0">
            <h1 class="text-sm text-[#687cca]">{{ halftimeScore.team3 }}</h1>
            <img src="/img/icons/switch.svg" :alt="$t('coach.alt.switch_sides')" class="w-5 h-5" />
            <h1 class="text-sm text-[#e38617]">{{ halftimeScore.team2 }}</h1>
          </div>

          <div
            class="flex divide-x divide-gray-950 bg-gray-900 min-w-max shrink-0 overflow-hidden flex-1"
            :class="[currentRound > 24 ? 'rounded-r-lg' : '']">
            <div class="flex divide-x divide-gray-950 min-w-max flex-1">
              <div
                v-for="(round, index) in secondHalfRounds"
                :key="index"
                @click="updateRound(index + 13)"
                class="flex flex-col p-3 text-text-secondary relative items-center justify-center w-12 shrink-0 cursor-pointer flex-1"
                :class="[currentRound === index + 13 ? 'bg-gradient-to-b from-gray-900 to-gray-800' : '']">
                <h1 class="text-text-secondary text-sm whitespace-nowrap">{{ index + 13 }}</h1>
                <img
                  :src="`/img/icons/${getRoundEndReasonString(round.winning_reason)}.svg`"
                  :alt="$t('coach.alt.round_win_icon')"
                  class="w-4 h-4 absolute"
                  :class="getWinIconPosition(round, index + 13)"
                  style="left: 50%; transform: translateX(-50%)"
                />
              </div>
            </div>
          </div>

          <div v-if="overtimeRounds" class="flex flex-col gap-2 items-center justify-center px-3 shrink-0">
            <h1 class="text-sm text-[#e38617]">{{ regulationScore.team2 }}</h1>
            <img src="/img/icons/switch.svg" :alt="$t('coach.alt.switch_sides')" class="w-5 h-5" />
            <h1 class="text-sm text-[#687cca]">{{ regulationScore.team3 }}</h1>
          </div>

          <div v-if="overtimeRounds" class="flex" v-for="(groupedRounds, group_index) in overtimeRounds" :key="group_index">
            <div
              class="flex divide-x divide-gray-950 bg-gray-900 min-w-max shrink-0 overflow-hidden flex-1"
              :class="[group_index % 2 === 1 && group_index === overtimeRounds.length - 1 ? 'rounded-r-lg' : '']">
              <div class="flex divide-x divide-gray-950 min-w-max flex-1">
                <div
                  v-for="(round, index) in groupedRounds.rounds"
                  :key="index"
                  @click="updateRound(index + groupedRounds.startingRound)"
                  class="flex flex-col p-3 text-text-secondary relative items-center justify-center w-12 shrink-0 cursor-pointer flex-1"
                  :class="[currentRound === index + groupedRounds.startingRound ? 'bg-gradient-to-b from-gray-900 to-gray-800' : '']">
                  <h1 class="text-text-secondary text-sm whitespace-nowrap">{{ index + groupedRounds.startingRound }}</h1>
                  <img
                    :src="`/img/icons/${getRoundEndReasonString(round.winning_reason)}.svg`"
                    :alt="$t('coach.alt.round_win_icon')"
                    class="w-4 h-4 absolute"
                    :class="getWinIconPosition(round, index + groupedRounds.startingRound)"
                    style="left: 50%; transform: translateX(-50%)"
                  />
                </div>
              </div>
            </div>
            <div v-if="overtimeRounds && group_index < overtimeRounds.length - 1" class="flex px-3 flex-col gap-2 items-center justify-center px-2 shrink-0">
              <h1 class="text-sm" :class="[group_index % 2 === 0 ? 'text-[#e38617]' : 'text-[#687cca]']">
                {{ getOvertimeHalfTimeScore(group_index)[group_index % 2 === 0 ? 'team3' : 'team2'] }}
              </h1>
              <img src="/img/icons/switch.svg" :alt="$t('coach.alt.switch_sides')" class="w-5 h-5" />
              <h1 class="text-sm" :class="[group_index % 2 === 0 ? 'text-[#687cca]' : 'text-[#e38617]']">
                {{ getOvertimeHalfTimeScore(group_index)[group_index % 2 === 0 ? 'team2' : 'team3'] }}
              </h1>
            </div>
          </div>

        </div>
      </div>

      <div
        v-if="roundState === 'loading' || roundState === 'preparing'"
        class="flex flex-col gap-2 justify-center items-center h-full"
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="animate-spin"
        >
          <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
          <path
            d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
            stroke="#4E54C8"
            stroke-width="2"
          />
        </svg>
        <p v-if="roundState === 'preparing'" class="text-text-secondary text-sm" data-test="round-preparing">
          {{ $t('coach.match_restrat.preparing_round', { n: currentRound }) }}
        </p>
      </div>
      <div
        v-else-if="roundState === 'processing' || roundState === 'gone' || roundState === 'failed'"
        class="flex flex-col gap-3 justify-center items-center py-6"
        data-test="round-error"
      >
        <p v-if="roundState === 'processing'" class="text-text-secondary text-sm">
          {{ $t('coach.match_restrat.reprocessing') }}
        </p>
        <p v-else-if="roundState === 'gone'" class="text-text-secondary text-sm">
          {{ $t('coach.match_restrat.round_gone', { n: currentRound }) }}
        </p>
        <p v-else class="text-text-secondary text-sm">{{ $t('coach.match_restrat.could_not_load_round', { n: currentRound }) }}</p>
        <button
          v-if="roundState !== 'gone'"
          class="px-4 py-2 bg-gray-800 hover:bg-gray-700 duration-300 rounded-md text-text-primary text-sm"
          data-test="round-retry"
          @click="loadRound(currentRound)"
        >
          {{ $t('coach.match_restrat.try_again') }}
        </button>
      </div>
      <div v-else class="gap-6 flex-col flex sm:flex-row">
        <div class="flex flex-col divide-y divide-gray-950 bg-gray-900 rounded-lg w-full">
          <div class="flex gap-3 p-3 items-center w-full">
            <img :src="`/img/icons/${isTeamOnCT(currentRound, 0) ? 'ct' : 't'}-badge.png`" :alt="$t('coach.match_restrat.team_1')" class="w-6 h-6" />
            <h1 class="text-text-primary text-sm flex-1">{{ matchData?.parsed_data?.team_info[0]?.team_name || $t('coach.match_restrat.team_1') }}</h1>
            <h1 class="text-text-primary text-sm">{{ roundData?.cTScore }}</h1>
          </div>
          <div class="flex flex-col gap-1 p-3" v-for="(player, index) in team1Players" :key="index">
            <div class="flex gap-2 items-center">
              <h1 class="text-text-primary text-sm">{{ player.playerName }}</h1>
            </div>
            <div class="flex gap-2 items-center">
              <img
                v-if="player.hasHelmet"
                class="h-4 brightness-75"
                src="../../../../assets/icons/headarmor.svg"
              />
              <img
                v-else-if="player.armor > 0"
                class="h-4 brightness-75"
                src="../../../../assets/icons/armor.svg"
              />
              <img
                v-if="player.hasDefuse"
                class="h-3 brightness-75"
                src="../../../../assets/icons/defuser.svg"
              />
              <h1 class="text-text-secondary text-xs">${{ player.cash }}</h1>
              <h1 class="text-danger-500 text-xs flex-1">-${{ player.cashSpentThisRound }}</h1>

              <div v-for="item in getSortedInventory(player.inventory)">
                <template v-if="item.weaponClass === 'Grenade' && getPrimary(player).weaponName !== item.weaponName">
                  <template v-if="item.weaponName === 'Flashbang' && player.flashbangAmmo === 2">
                    <div class="flex gap-0.5">
                      <img
                        class="h-4 brightness-75"
                        :src="`/img/icons/${item.weaponName}.png`"
                      />
                      <img
                        class="h-4 brightness-75"
                        :src="`/img/icons/${item.weaponName}.png`"
                      />
                    </div>
                  </template>
                  <img
                    v-else
                    class="h-4 brightness-75"
                    :src="`/img/icons/${item.weaponName}.png`"
                  />
                </template>
              </div>

              <img
                v-if="getPrimary(player)"
                class="h-4"
                :src="`/img/icons/${getPrimary(player).weaponName}.png`"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col divide-y divide-gray-950 bg-gray-900 rounded-lg w-full">
          <div class="flex gap-3 p-3 items-center w-full">
            <img :src="`/img/icons/${isTeamOnCT(currentRound, 1) ? 'ct' : 't'}-badge.png`" :alt="$t('coach.match_restrat.team_1')" class="w-6 h-6" />
            <h1 class="text-text-primary text-sm flex-1">{{ matchData?.parsed_data?.team_info[1]?.team_name || $t('coach.match_restrat.team_2') }}</h1>
            <h1 class="text-text-primary text-sm">{{ roundData?.tScore }}</h1>
          </div>
          <div class="flex flex-col gap-1 p-3" v-for="(player, index) in team2Players" :key="index">
            <div class="flex gap-2 items-center">
              <h1 class="text-text-primary text-sm">{{ player.playerName }}</h1>
            </div>
            <div class="flex gap-2 items-center">
              <img
                v-if="player.hasHelmet"
                class="h-4 brightness-75"
                src="../../../../assets/icons/headarmor.svg"
              />
              <img
                v-else-if="player.armor > 0"
                class="h-4 brightness-75"
                src="../../../../assets/icons/armor.svg"
              />
              <img
                v-if="player.hasDefuse"
                class="h-3 brightness-75"
                src="../../../../assets/icons/defuser.svg"
              />
              <h1 class="text-text-secondary text-xs">${{ player.cash }}</h1>
              <h1 class="text-danger-500 text-xs flex-1">-${{ player.cashSpentThisRound }}</h1>

              <div v-for="item in getSortedInventory(player.inventory)">
                <template v-if="item.weaponClass === 'Grenade' && getPrimary(player).weaponName !== item.weaponName">
                  <template v-if="item.weaponName === 'Flashbang' && player.flashbangAmmo === 2">
                    <div class="flex gap-0.5">
                      <img
                        class="h-4 brightness-75"
                        :src="`/img/icons/${item.weaponName}.png`"
                      />
                      <img
                        class="h-4 brightness-75"
                        :src="`/img/icons/${item.weaponName}.png`"
                      />
                    </div>
                  </template>
                  <img
                    v-else
                    class="h-4 brightness-75"
                    :src="`/img/icons/${item.weaponName}.png`"
                  />
                </template>
              </div>

              <img
                v-if="getPrimary(player)"
                class="h-4"
                :src="`/img/icons/${getPrimary(player).weaponName}.png`"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  defineEmits,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { HandThumbUpIcon } from '@heroicons/vue/20/solid';
import { useRestratRound } from '../../../../composables/useRestratRound';

const store = useStore();
const emit = defineEmits(['start-demo']);
const currentRound = ref(1);
const activeServer = computed(() => store.getters.activeServer);
const props = defineProps({
  matchData: {
    type: Object,
    required: true,
  },
  loadingRound: {
    type: Boolean,
    required: true,
  },
  loadingRoundSuccess: {
    type: Boolean,
    required: true,
  },
  hide2DViewer: {
    type: Boolean,
    default: false,
  },
});

function startDemo() {
  emit('start-demo', currentRound.value);
}

function open2DViewer() {
  // Open 2D viewer in new window/tab
  const viewerUrl = `/demos/${props.matchData?.id}/review?round=${currentRound.value}`;
  window.open(viewerUrl, '_blank');
}

const {
  roundData, roundState, loadRound, cancel: cancelRoundLoad,
} = useRestratRound({
  demoId: () => props.matchData?.id,
  teamId: () => store.state.activeTeam?.id,
  isDemoReady: () => props.matchData?.status === 'ready',
});

// A different demo (pro series map switch) or a finished re-parse invalidates the round on screen.
watch(
  () => [props.matchData?.id, props.matchData?.status],
  ([id, status], [oldId, oldStatus]) => {
    if (id !== oldId || (status === 'ready' && oldStatus !== 'ready')) loadRound(currentRound.value);
  },
);

const firstHalfRounds = computed(() => processedRoundInfo.value.slice(0, 12));

const secondHalfRounds = computed(() => processedRoundInfo.value.slice(12, 24));

const overtimeRounds = computed(() => {
  const totalRounds = processedRoundInfo.value.length || 0;

  if (totalRounds <= 24) {
    return null; // No overtime
  }

  // Get all rounds after round 24
  const allOvertimeRounds = processedRoundInfo.value.slice(24);

  // Split overtime rounds into groups of three
  const groupedRounds = [];
  for (let i = 0; i < allOvertimeRounds.length; i += 3) {
    const threeRounds = allOvertimeRounds.slice(i, i + 3);
    if (threeRounds.length === 3) { // Only add complete sets of three
      groupedRounds.push({
        rounds: threeRounds,
        team1Wins: threeRounds.filter((round) => round.winning_team === (i % 2 === 0 ? 2 : 3)).length,
        team2Wins: threeRounds.filter((round) => round.winning_team === (i % 2 === 0 ? 3 : 2)).length,
        startingRound: i + 25, // Round number starts at 25 for overtime
      });
    }
  }
  return groupedRounds;
});

const regulationScore = computed(() => ({
  team2: 12,
  team3: 12,
}));

// Process round data to create a clean sequential array where index = round number - 1
const processedRoundInfo = computed(() => {
  const rawRounds = props.matchData?.parsed_data?.round_info || [];

  // Group rounds by their round_num to handle duplicates
  const roundGroups = {};
  rawRounds.forEach((round) => {
    if (!roundGroups[round.round_num]) {
      roundGroups[round.round_num] = [];
    }
    roundGroups[round.round_num].push(round);
  });

  // Create sequential array - for duplicates, take the last entry
  const processedRounds = [];
  const maxRoundNum = Math.max(...Object.keys(roundGroups).map(Number));

  for (let i = 1; i <= maxRoundNum; i++) {
    if (roundGroups[i] && roundGroups[i].length > 0) {
      // Take the last entry for this round number
      const roundData = roundGroups[i][roundGroups[i].length - 1];
      processedRounds.push(roundData);
    }
  }

  return processedRounds;
});

// winning_team represents SIDES, not fixed team indices
// winning_team 2 = T side, winning_team 3 = CT side (based on typical CS2 convention)
const getWinningSide = (winningTeamNumber) => (winningTeamNumber === 2 ? 'T' : winningTeamNumber === 3 ? 'CT' : 'T');

// Get the team name that was on the winning side for a specific round
const getWinningTeamName = (round, roundNumber) => {
  const roundInfo = processedRoundInfo.value[roundNumber - 1];
  if (!roundInfo) return '';

  const winningSide = getWinningSide(round.winning_team);

  if (winningSide === 'CT') {
    return roundInfo.ct_team;
  }
  return roundInfo.t_team;
};

function isTeamOnCT(roundNumber, teamIndex) {
  // Get the round data (roundNumber 1 = index 0)
  const roundInfo = processedRoundInfo.value[roundNumber - 1];

  if (!roundInfo) {
    return false; // Default to T side if no data
  }

  // Get the team name for the given team_index
  const teamName = props.matchData?.parsed_data?.team_info?.[teamIndex]?.team_name;

  if (!teamName) {
    return false; // Default to T side if no team name
  }

  // Check if this team's name matches the CT team for this round
  return roundInfo.ct_team === teamName;
}

function getWinIconPosition(round, roundNumber) {
  // Get which side won (CT or T)
  const winningSide = getWinningSide(round.winning_team);

  // Get the round info to see which team is on which side
  const roundInfo = processedRoundInfo.value[roundNumber - 1];
  if (!roundInfo) {
    return 'top-3'; // Default position
  }

  // The navigation header currently shows:
  // Top row: team_info[1]
  // Bottom row: team_info[0]

  // Check which side team_info[1] is on (top row)
  const team1IsOnCT = isTeamOnCT(roundNumber, 1);

  if (winningSide === 'CT') {
    // CT side won - show icon where the CT team is positioned
    return team1IsOnCT ? 'top-3' : 'bottom-3';
  }
  // T side won - show icon where the T team is positioned
  return team1IsOnCT ? 'bottom-3' : 'top-3';
}

function getOvertimeHalfTimeScore(index) {
  let team2Score = 0;
  let team3Score = 0;

  // Get all overtime rounds up to current index
  const overtimeRoundsToIndex = overtimeRounds.value?.slice(0, index + 1) || [];

  // Sum up scores from each overtime period
  overtimeRoundsToIndex.forEach((period) => {
    team2Score += period.team2Wins;
    team3Score += period.team1Wins;
  });

  return {
    team2: team2Score + regulationScore.value.team2,
    team3: team3Score + regulationScore.value.team3,
  };
}

const halftimeScore = computed(() => {
  let team2Score = 0;
  let team3Score = 0;

  // Get first 12 rounds
  const firstHalf = processedRoundInfo.value.slice(0, 12);

  // Count wins for each team
  firstHalf.forEach((round) => {
    if (round.winning_team === 2) {
      team2Score++;
    } else if (round.winning_team === 3) {
      team3Score++;
    }
  });

  return {
    team2: team2Score,
    team3: team3Score,
  };
});

const switchTeams = computed(() => overtimeRounds.value?.length % 2 === 0);

const team1Players = computed(() => {
  if (!roundData.value?.frames[0]) return [];

  // If team0 is CT, get CT players, otherwise get T players
  return (isTeamOnCT(currentRound.value, 0)
    ? roundData.value.frames[0].ct.players
    : roundData.value.frames[0].t.players)
    ?.filter((player) => player.hp > 0)
    ?.sort((a, b) => {
      if (a.playerName?.toLowerCase() < b.playerName?.toLowerCase()) return -1;
      if (a.playerName?.toLowerCase() > b.playerName?.toLowerCase()) return 1;
      return 0;
    });
});

const team2Players = computed(() => {
  if (!roundData.value?.frames[0]) return [];

  // If team1 is CT, get CT players, otherwise get T players
  return (isTeamOnCT(currentRound.value, 1)
    ? roundData.value.frames[0].ct.players
    : roundData.value.frames[0].t.players)
    ?.filter((player) => player.hp > 0)
    ?.sort((a, b) => {
      if (a.playerName?.toLowerCase() < b.playerName?.toLowerCase()) return -1;
      if (a.playerName?.toLowerCase() > b.playerName?.toLowerCase()) return 1;
      return 0;
    });
});

function updateRound(round) {
  currentRound.value = round;
  loadRound(round);
}

function getPrimary(player) {
  let primary;
  player.inventory.forEach((item) => {
    if (item.weaponClass === 'Rifle') {
      primary = item;
    } else if (
      item.weaponClass !== 'Pistols'
      && item.weaponClass !== 'Grenade'
      && item.weaponClass !== 'Equipment'
    ) {
      primary = item;
    } else if (item.weaponClass === 'Pistols' && !primary) {
      primary = item;
    }
  });

  if (!primary) return player.inventory[0];

  return primary;
}

function getSortedInventory(inventory) {
  const grenadeOrder = ['C4', 'Decoy Grenade', 'Flashbang', 'HE Grenade', 'Molotov', 'Incendiary Grenade', 'Smoke Grenade'];

  return inventory.sort((a, b) => grenadeOrder.indexOf(a.weaponName) - grenadeOrder.indexOf(b.weaponName));
}

function getRoundEndReasonString(reason) {
  switch (reason) {
    case 1:
      return 'texplode';
    case 7:
      return 'ctdefuse';
    case 8:
      return 'ctskull';
    case 9:
      return 'tskull';
    case 12:
      return 'cttime';
    default:
      return '';
  }
}

onMounted(() => {
  loadRound(currentRound.value);
});

onBeforeUnmount(() => {
  cancelRoundLoad();
});

</script>
